(() => {
  const normalize = (value) => String(value || '').trim().toLocaleLowerCase();

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active || 'USD'
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  if (!customElements.get('mori-knot-builder')) {
    customElements.define('mori-knot-builder', class MoriKnotBuilder extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        this.previewFrame = this.querySelector('[data-mk2-builder-preview]');
        this.baseImage = this.querySelector('[data-mk2-builder-base]');
        this.overlayImage = this.querySelector('[data-mk2-builder-overlay]');
        this.variantInput = this.querySelector('[data-mk2-builder-variant]');
        this.colorProperty = this.querySelector('[data-mk2-builder-color-property]');
        this.charmProperty = this.querySelector('[data-mk2-builder-charm-property]');
        this.price = this.querySelector('[data-mk2-builder-price]');
        this.submit = this.querySelector('.mk2b__submit[type="submit"]');
        this.addText = this.querySelector('[data-mk2-builder-add-text]');
        this.status = this.querySelector('[data-mk2-builder-status]');
        this.colorSummary = this.querySelector('[data-mk2-builder-color-summary]');
        this.charmSummary = this.querySelector('[data-mk2-builder-charm-summary]');
        this.colorInputs = [...this.querySelectorAll('[data-mk2-builder-color]')];
        this.charmInputs = [...this.querySelectorAll('[data-mk2-builder-charm]')];
        this.defaultBase = this.baseImage?.currentSrc || this.baseImage?.src || '';
        this.variants = [];

        const variantsNode = this.querySelector('[data-mk2-builder-variants]');
        if (variantsNode) {
          try {
            this.variants = JSON.parse(variantsNode.textContent) || [];
          } catch (error) {
            this.variants = [];
          }
        }

        this.combinations = new Map();
        this.querySelectorAll('[data-mk2-builder-combination]').forEach((item) => {
          const key = this.combinationKey(item.dataset.color, item.dataset.charm);
          if (item.dataset.preview) this.combinations.set(key, item.dataset.preview);
        });

        [...this.colorInputs, ...this.charmInputs].forEach((input) => {
          input.addEventListener('change', () => this.update(), { signal });
        });

        this.update();
        this.dataset.ready = 'true';
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      combinationKey(color, charm) {
        return `${normalize(color)}::${normalize(charm)}`;
      }

      selected(inputs) {
        return inputs.find((input) => input.checked) || inputs[0] || null;
      }

      variantFor(colorValue, charmValue) {
        if (!this.variants.length) return null;
        const color = normalize(colorValue);
        const charm = normalize(charmValue);
        const includes = (variant, value) => (variant.options || []).some((option) => normalize(option) === value);
        const colorIsOption = Boolean(color) && this.variants.some((variant) => includes(variant, color));
        const charmIsOption = Boolean(charm) && this.variants.some((variant) => includes(variant, charm));

        const exact = this.variants.filter((variant) => {
          if (colorIsOption && !includes(variant, color)) return false;
          if (charmIsOption && !includes(variant, charm)) return false;
          return true;
        });
        const initialId = String(this.variantInput?.value || '');
        return exact.find((variant) => variant.available)
          || exact[0]
          || this.variants.find((variant) => String(variant.id) === initialId)
          || this.variants.find((variant) => variant.available)
          || this.variants[0];
      }

      imageUrlFromVariant(variant) {
        if (!variant?.featured_image) return '';
        if (typeof variant.featured_image === 'string') return variant.featured_image;
        return variant.featured_image.src || '';
      }

      swapImage(image, url, hideWhenEmpty = false) {
        if (!image) return;
        image.dataset.requestedUrl = url || '';
        if (!url) {
          if (hideWhenEmpty) image.hidden = true;
          return;
        }
        if (image.dataset.currentUrl === url) {
          image.hidden = false;
          return;
        }

        this.previewFrame?.setAttribute('aria-busy', 'true');
        const loader = new Image();
        loader.onload = () => {
          if (image.dataset.requestedUrl !== url) return;
          image.src = url;
          image.removeAttribute('srcset');
          image.dataset.currentUrl = url;
          image.hidden = false;
          this.previewFrame?.removeAttribute('aria-busy');
        };
        loader.onerror = () => {
          if (image.dataset.requestedUrl === url) this.previewFrame?.removeAttribute('aria-busy');
        };
        loader.src = url;
      }

      update() {
        const colorInput = this.selected(this.colorInputs);
        const charmInput = this.selected(this.charmInputs);
        if (colorInput && !colorInput.checked) colorInput.checked = true;
        if (charmInput && !charmInput.checked) charmInput.checked = true;

        const colorLabel = colorInput?.value || 'Default';
        const charmLabel = charmInput?.value || 'No charm';
        const colorVariantValue = colorInput?.dataset.variantValue || colorLabel;
        const charmVariantValue = charmInput?.dataset.variantValue || charmLabel;
        const variant = this.variantFor(colorVariantValue, charmVariantValue);
        const combination = this.combinations.get(this.combinationKey(colorLabel, charmLabel));
        const baseUrl = combination
          || colorInput?.dataset.preview
          || this.imageUrlFromVariant(variant)
          || this.defaultBase;
        const overlayUrl = combination ? '' : charmInput?.dataset.overlay || '';

        this.swapImage(this.baseImage, baseUrl);
        this.overlayImage?.classList.toggle('mk2b__overlay--packaged', charmInput?.dataset.packagedCharm === 'true');
        this.swapImage(this.overlayImage, overlayUrl, true);
        if (!overlayUrl && this.overlayImage) this.overlayImage.hidden = true;

        if (this.colorSummary) this.colorSummary.textContent = colorLabel;
        if (this.charmSummary) this.charmSummary.textContent = charmLabel;
        if (this.colorProperty) this.colorProperty.value = colorLabel;
        if (this.charmProperty) this.charmProperty.value = charmLabel;

        if (variant) {
          if (this.variantInput) this.variantInput.value = variant.id;
          if (this.price) this.price.textContent = formatMoney(variant.price, this.dataset.currency);
          if (this.submit) {
            this.submit.disabled = !variant.available;
            if (variant.available) this.submit.removeAttribute('aria-disabled');
            else this.submit.setAttribute('aria-disabled', 'true');
          }
          if (this.addText && this.submit) {
            this.addText.textContent = variant.available ? this.submit.dataset.addLabel : this.submit.dataset.soldLabel;
          }
          if (this.status) {
            this.status.textContent = variant.available
              ? `${colorLabel} with ${charmLabel} is ready to add.`
              : `${colorLabel} with ${charmLabel} is currently sold out.`;
          }
        } else if (this.status) {
          this.status.textContent = `${colorLabel} with ${charmLabel} selected.`;
        }
      }
    });
  }

  if (!customElements.get('mori-knot-film')) {
    customElements.define('mori-knot-film', class MoriKnotFilm extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const signal = this.abortController.signal;
        this.dialog = this.querySelector('[data-mk2-film-dialog]');
        this.slot = this.querySelector('[data-mk2-film-slot]');
        this.openButtons = [...this.querySelectorAll('[data-mk2-film-open]')];
        this.closeButton = this.querySelector('[data-mk2-film-close]');

        this.openButtons.forEach((button) => button.addEventListener('click', () => this.open(button), { signal }));
        this.closeButton?.addEventListener('click', () => this.close(), { signal });
        this.dialog?.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        }, { signal });
        this.dialog?.addEventListener('close', () => this.pause(), { signal });
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.pause();
        if (this.dialog?.open) this.dialog.close();
      }

      desiredMode() {
        return window.matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
      }

      ensureVideo() {
        const mode = this.desiredMode();
        if (this.loadedMode === mode && this.slot?.querySelector('video')) return this.slot.querySelector('video');
        const preferred = this.querySelector(`template[data-mk2-film-template="${mode}"]`);
        const fallback = this.querySelector('template[data-mk2-film-template]');
        const template = preferred || fallback;
        if (!template || !this.slot) return null;
        this.pause();
        this.slot.replaceChildren(template.content.cloneNode(true));
        this.loadedMode = mode;
        return this.slot.querySelector('video');
      }

      open(trigger) {
        if (!this.dialog || this.dialog.open) return;
        const video = this.ensureVideo();
        if (!video) return;
        this.lastTrigger = trigger;
        this.dialog.showModal();
        video.play().catch(() => {});
      }

      pause() {
        this.slot?.querySelectorAll('video').forEach((video) => video.pause());
      }

      close() {
        this.pause();
        if (this.dialog?.open) this.dialog.close();
        if (this.lastTrigger?.isConnected) this.lastTrigger.focus({ preventScroll: true });
      }
    });
  }

  if (!customElements.get('mori-knot-community')) {
    customElements.define('mori-knot-community', class MoriKnotCommunity extends HTMLElement {
      connectedCallback() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const signal = this.abortController.signal;
        this.rail = this.querySelector('[data-mk2-community-rail]');
        this.previous = this.querySelector('[data-mk2-community-previous]');
        this.next = this.querySelector('[data-mk2-community-next]');
        if (!this.rail || !this.previous || !this.next) return;

        this.previous.addEventListener('click', () => this.move(-1), { signal });
        this.next.addEventListener('click', () => this.move(1), { signal });
        this.rail.addEventListener('scroll', () => this.scheduleUpdate(), { signal, passive: true });
        this.rail.addEventListener('keydown', (event) => this.onKeydown(event), { signal });

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.updateControls());
          this.resizeObserver.observe(this.rail);
        }
        this.updateControls();
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.resizeObserver?.disconnect();
        if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
      }

      behavior() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      }

      move(direction) {
        const item = this.rail.querySelector('.mk2c__item');
        const gap = parseFloat(getComputedStyle(this.rail).columnGap || 0);
        const distance = item ? item.getBoundingClientRect().width + gap : this.rail.clientWidth * 0.8;
        this.rail.scrollBy({ left: direction * distance, behavior: this.behavior() });
      }

      onKeydown(event) {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(event.key === 'ArrowLeft' ? -1 : 1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.rail.scrollTo({
            left: event.key === 'Home' ? 0 : this.rail.scrollWidth,
            behavior: this.behavior()
          });
        }
      }

      scheduleUpdate() {
        if (this.updateFrame) return;
        this.updateFrame = requestAnimationFrame(() => {
          this.updateFrame = null;
          this.updateControls();
        });
      }

      updateControls() {
        const maximum = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
        this.previous.disabled = this.rail.scrollLeft <= 2;
        this.next.disabled = this.rail.scrollLeft >= maximum - 2;
      }
    });
  }
})();
